/*! Copyright (c) Safe As Milk. All rights reserved. */
import { debounce } from "utils";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

class ScrollingMarquee extends HTMLElement {
    #debouncedSetMarqueeSpeed;
    #paused = false;
    #onToggleClick;
    #onMotionPreferenceChange;
    constructor() {
        super();
        this.#debouncedSetMarqueeSpeed = debounce(this.#setMarqueeSpeed.bind(this), 50);
        this.#onToggleClick = this.#toggle.bind(this);
        this.#onMotionPreferenceChange = this.#syncWithMotionPreference.bind(this);
    }
    connectedCallback() {
        this.marqueeContainer = this.querySelector(".marquee");
        this.marqueeElements = Array.from(this.querySelectorAll(".marquee__content"));
        this.toggleButton = this.querySelector("[data-marquee-toggle]");
        this.resizeObserver = new ResizeObserver(this.#debouncedSetMarqueeSpeed);
        this.resizeObserver.observe(this.marqueeContainer);
        this.#setMarqueeSpeed();
        if (this.toggleButton) {
            this.toggleButton.addEventListener("click", this.#onToggleClick);
            this.motionPreference = window.matchMedia ? window.matchMedia(REDUCED_MOTION_QUERY) : null;
            if (this.motionPreference) {
                if (typeof this.motionPreference.addEventListener === "function") {
                    this.motionPreference.addEventListener("change", this.#onMotionPreferenceChange);
                } else if (typeof this.motionPreference.addListener === "function") {
                    this.motionPreference.addListener(this.#onMotionPreferenceChange);
                }
            }
            this.#setPaused(Boolean(this.motionPreference && this.motionPreference.matches));
        }
    }
    disconnectedCallback() {
        this.resizeObserver.unobserve(this.marqueeContainer);
        if (this.toggleButton) {
            this.toggleButton.removeEventListener("click", this.#onToggleClick);
        }
        if (this.motionPreference) {
            if (typeof this.motionPreference.removeEventListener === "function") {
                this.motionPreference.removeEventListener("change", this.#onMotionPreferenceChange);
            } else if (typeof this.motionPreference.removeListener === "function") {
                this.motionPreference.removeListener(this.#onMotionPreferenceChange);
            }
        }
    }
    #syncWithMotionPreference(event) {
        this.#setPaused(Boolean(event.matches));
    }
    #toggle() {
        this.#setPaused(!this.#paused);
    }
    #setPaused(paused) {
        this.#paused = paused;
        this.classList.toggle("is-paused", paused);
        this.classList.toggle("is-playing", !paused);
        if (!this.toggleButton) return;
        this.toggleButton.setAttribute("aria-pressed", paused ? "true" : "false");
        const label = paused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause;
        if (label) this.toggleButton.setAttribute("aria-label", label);
    }
    #setMarqueeSpeed() {
        const marqueeComputedStyle = window.getComputedStyle(this.marqueeContainer);
        const gapValue = parseInt(marqueeComputedStyle.getPropertyValue("column-gap"), 10);
        const speed = (parseFloat(this.getAttribute("marquee-speed")) || 0) * 10;
        this.marqueeElements.forEach((element => {
            const elementWidth = element.offsetWidth + gapValue;
            const timeTaken = elementWidth / speed;
            element.style.animationDuration = `${timeTaken}s`;
        }));
    }
}

customElements.define("scrolling-marquee", ScrollingMarquee);
